/**
 * Date utilities for Analytics components
 */

// Regex for YYYY-MM format validation
export const MONTH_FORMAT_REGEX = /^[0-9]{4}-(0[1-9]|1[012])$/;

// Regex for YYYYMM format validation (API format)
export const MONTH_API_FORMAT_REGEX = /^[0-9]{6}$/;

/**
 * Get recent months in YYYYMM format (for Analytics APIs)
 * @param count Number of months to get
 * @returns Array of months in YYYYMM format, oldest to newest
 */
export const getRecentMonths = (count = 6): string[] => {
    const months: string[] = [];
    const today = new Date();

    // Start from last month (current month usually incomplete)
    let currentMonth = today.getMonth() - 1;
    let currentYear = today.getFullYear();

    // Handle year rollover
    if (currentMonth < 0) {
        currentMonth = 11;
        currentYear--;
    }

    for (let i = 0; i < count; i++) {
        const monthStr = (currentMonth + 1).toString().padStart(2, '0');
        months.push(`${currentYear}${monthStr}`);

        currentMonth--;
        if (currentMonth < 0) {
            currentMonth = 11;
            currentYear--;
        }
    }

    return months.reverse(); // Oldest to newest
};

/**
 * Format YYYYMM to YYYY-MM for display
 */
export const formatMonthForDisplay = (month: string): string => {
    if (month.length === 6) {
        return `${month.substring(0, 4)}-${month.substring(4, 6)}`;
    }
    return month;
};

/**
 * Convert YYYY-MM to YYYYMM for API
 */
export const formatMonthForApi = (month: string): string => {
    return month.replace('-', '');
};

/**
 * Validate month format (supports both YYYY-MM and YYYYMM)
 */
export const validateMonth = (month: string, format: 'display' | 'api' = 'display'): string => {
    if (!month) return '';

    const regex = format === 'display' ? MONTH_FORMAT_REGEX : MONTH_API_FORMAT_REGEX;
    const apiFormat = format === 'display' ? formatMonthForApi(month) : month;

    if (!regex.test(format === 'display' ? month : apiFormat)) {
        return format === 'display' ? 'Must be in YYYY-MM format' : 'Must be in YYYYMM format';
    }

    // Additional validation: check if month is reasonable
    const monthNum = parseInt(apiFormat.substring(4, 6));

    if (monthNum < 1 || monthNum > 12) {
        return 'Month must be between 01-12';
    }

    return '';
};

/**
 * Generate array of months between start and end (inclusive)
 * @param startMonth Start month in YYYYMM format
 * @param endMonth End month in YYYYMM format
 * @returns Array of month strings in YYYYMM format
 */
export const generateMonthRange = (startMonth: string, endMonth: string): string[] => {
    const months: string[] = [];
    let currentMonth = parseInt(startMonth.substring(4, 6)) - 1; // 0-based
    let currentYear = parseInt(startMonth.substring(0, 4));
    const endMonthNum = parseInt(endMonth.substring(4, 6)) - 1;
    const endYear = parseInt(endMonth.substring(0, 4));

    while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonthNum)) {
        const monthStr = (currentMonth + 1).toString().padStart(2, '0');
        months.push(`${currentYear}${monthStr}`);

        currentMonth++;
        if (currentMonth > 11) {
            currentMonth = 0;
            currentYear++;
        }
    }

    return months;
};
